import { ConnectorModel, CreateConnectorModel } from '../models/connector/connector.model';
import { Dataset, DtFilter, DtSort } from '@/components/data-table/helpers';
import { CreateResponse, DeleteResponse, PatchResponse, SAFE_LIMIT } from '.';
import { create, deleteApi, get, getBinaryFile, patch } from './sp-api.service';
import { downloadFile, JsonObject } from '../helpers';
import { SharepointConnectorModel } from '../models/connector/sharepoint-connector.model';
import { getArrayParam } from '../url-helpers';
import { kebabCase, orderBy } from 'lodash';
import { ContextProviderModel } from '../models/context-provider/context-provider.model';
import { PrivateModelModel } from '../models/private-model/private-model.model';
import { ConnectorType, getTypedConnector } from '../models/connector';

export const getConnectors = async (
  page: number,
  pageSize: number,
  dtSort?: DtSort,
  dtFilter?: DtFilter
): Promise<Dataset<ConnectorModel>> => {
  const response = await get('/connector-controller/connector');
  let rows = ((response as JsonObject[]) || []).map((data) => new ConnectorModel(data));

  // Filter
  if (dtFilter) {
    const { keyword } = dtFilter;
    if (keyword) {
      const safeKeyword = String(keyword).trim().toLowerCase();
      rows = rows.filter(({ name }) => String(name).toLowerCase().includes(safeKeyword));
    }
  }

  // Sort
  if (dtSort) {
    const { columnName, direction } = dtSort;
    rows = orderBy(rows, [columnName], [direction]);
  }

  // Paginate
  const startIndex = page * pageSize;
  const total = rows?.length || 0;
  rows = rows.slice(startIndex, startIndex + pageSize);

  return {
    page,
    pageSize,
    rows,
    total,
  };
};

export const getAllConnectors = async (): Promise<ConnectorModel[]> =>
  (await getConnectors(0, SAFE_LIMIT)).rows;

export const getContextProviderConnectors = async (): Promise<ConnectorModel[]> => {
  const connectors = await getAllConnectors();

  return connectors.map(getTypedConnector).filter((connector) => {
    const { supportedFeatures, contextProviderTypes } = connector;
    return supportedFeatures.contextData && !!contextProviderTypes.length;
  });
};

export const getPrivateModelConnectors = async (): Promise<ConnectorModel[]> => {
  const connectors = await getAllConnectors();

  return connectors.map(getTypedConnector).filter((connector) => {
    const { supportedFeatures } = connector;
    return supportedFeatures.privateModels;
  });
};

export const getSiemConnectors = async (): Promise<ConnectorModel[]> => {
  const connectors = await getAllConnectors();

  return connectors.map(getTypedConnector).filter((connector) => {
    const { supportedFeatures } = connector;
    return supportedFeatures.siem;
  });
};

export const getConnectorsByType = async (
  connectorType: ConnectorType
): Promise<ConnectorModel[]> => {
  const connectors = await getAllConnectors();
  return connectors.filter(({ type }) => type === connectorType);
};

export const getConnectorById = async (connectorId: string): Promise<ConnectorModel | null> => {
  const response = await get(`/connector-controller/connector/${connectorId}`);
  return response ? new ConnectorModel(response as JsonObject) : null;
};

export const getConnectorsByIds = async (connectorIds: string[]): Promise<ConnectorModel[]> => {
  if (!connectorIds.length) {
    return [];
  }

  const params = getArrayParam('connectorId', connectorIds);
  const response = await get('/connector-controller/connector', params);
  return response ? (response as JsonObject[]).map((data) => new ConnectorModel(data)) : [];
};

export const getConnectorUsage = async (
  connectorId: string
): Promise<[ContextProviderModel[], PrivateModelModel[]]> => {
  const response = await get(`/connector-controller/connector/usage/${connectorId}`).catch(() => {
    // some connector types are not supported, causing the service to respond with a 500
    return null;
  });

  if (!response) {
    return [[], []];
  }

  const { contextProviders, privateModels } = response as {
    contextProviders: JsonObject[];
    privateModels: JsonObject[];
  };

  return [
    contextProviders.map((data) => new ContextProviderModel(data)),
    privateModels.map((data) => new PrivateModelModel(data)),
  ];
};

export const createConnector = async (model: ConnectorModel): Promise<CreateResponse> => {
  const createModel = new CreateConnectorModel(model);
  const response = await create(
    `/connector-controller/connector/${model.type}`,
    createModel._props
  );

  if (!response.created) {
    return response;
  }

  const connector = getTypedConnector(new ConnectorModel(response.data));

  return { created: true, model: connector };
};

export const patchConnector = async (model: ConnectorModel): Promise<PatchResponse> => {
  const createModel = new CreateConnectorModel(model);
  return patch(`/connector-controller/connector/${model.type}/${model.id}`, createModel._props);
};

export const deleteConnector = async (model: ConnectorModel): Promise<DeleteResponse> => {
  return deleteApi(`/connector-controller/connector/${model.type}/${model.id}`);
};

export const downloadProxyConnectorZip = async (connectorId: string, name: string) => {
  const blob = await getBinaryFile(`/connector-controller/connector/proxy/${connectorId}/files`);
  const filename = `f5-workforce-ai-security-${name ? kebabCase(name) : connectorId}-proxy-connector.zip`;
  return downloadFile(blob, filename);
};

export const downloadSharepointCert = (connector: SharepointConnectorModel) => {
  const { publicCert } = connector.config;

  if (!publicCert) {
    return;
  }

  const blob = new Blob([atob(publicCert)], { type: 'application/x-pem-file' });

  return downloadFile(blob, 'f5-workforce-ai-security-public-cert.crt');
};
